import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Form, Modal, notification, Space, Table } from 'antd';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { Route } from '@/routes/routes.type';
import { DeleteIcon, EditIcon, TabContentWrapper } from '@/components/_common';
import {
  Question,
  QuestionType,
} from '@/core/domain/crm/Questions/Questions.types';
import SearchQuestionnaireForm from '@/components/CRM/SearchQuestionnaire/SearchQuestionnaireForm';
import { DeleteButton, SaveButton } from '@/components/_common/Buttons';
import SearchQuestionnaireQuestionModal from '@/components/CRM/SearchQuestionnaire/SearchQuestionnaireQuestionModal';
import {
  CreatQuizzesData,
  Quiz,
  QuizFormValues,
} from '@/core/domain/crm/Quizzes/Quizzes.types';
import { detailQuizzes } from '@/core/domain/crm/Quizzes/Quizzes.service';
import {
  useCreateQuizzes,
  useEditQuizzes,
  useRemoveQuizzes,
} from '@/core/domain/crm/Quizzes/Quizzes.hooks';
import SearchQuestionnaireDetailContentTabs from '@/components/CRM/SearchQuestionnaire/DetailContentTabs';
import Authorities from '@/types/Authority';
import { getTableScrollX } from '@/utils/table';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { hasPermissions } from '@/utils/functions/hasPermissions';

interface Props {
  route: Route;
}

const SearchQuestionnaireCreatePage: React.FC<Props> = ({ route }) => {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<QuizFormValues>();
  const {
    params: { quizId },
    isExact,
  } = useRouteMatch<{ quizId: string }>();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionDetail, setQuestionDetail] = useState<Question>();
  const [quiz, setQuiz] = useState<Quiz>();
  const [loading, setLoading] = useState(false);
  const [questionModalVisible, setQuestionModalVisible] = useState(false);
  const [basePath, setBasePath] = useState('general');

  const createQuizzes = useCreateQuizzes();
  const editQuizzes = useEditQuizzes(Number(quizId));
  const removeQuizzes = useRemoveQuizzes();

  useEffect(() => {
    if (isExact && quizId) {
      history.replace(
        `/app/customers/crm/searchQuestionnaire/${quizId}/general`,
      );
    }
  }, [isExact, quizId]);

  useEffect(() => {
    if (quizId) {
      getQuiz();
    }
  }, [quizId]);

  const getQuiz = async () => {
    setLoading(true);
    try {
      const result = await detailQuizzes(Number(quizId));
      setQuestions(result.questions ?? []);
      setQuiz(result);
    } catch (error) {
      notification.error({ description: 'Error', message: 'error message' });
    }
    setLoading(false);
  };

  const toggleQuestionModal = (question?: Question) => {
    if (question) {
      setQuestionDetail(question);
    } else {
      setQuestionDetail(undefined);
    }
    setQuestionModalVisible(!questionModalVisible);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onCreateQuestion = (values: any) => {
    if (quizId) {
      history.push(`/app/customers/crm/searchQuestionnaire/${quizId}/general`);
      setBasePath('general');
    }
    setQuestions((prev) => [
      ...prev,
      {
        ...values,
        questionSequence: prev.length + 1,
        id: moment().unix(),
        isNew: true,
      },
    ]);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChangeQuestion = (values: any) => {
    setQuestions((prev) =>
      prev.map((item) =>
        values.id === item.id ? { ...item, ...values } : item,
      ),
    );
  };

  const handleRemoveQuestion = (question: Question) => {
    if (quizId) {
      Modal.confirm({
        title: 'Remover',
        content: 'Remover questão',
        onOk: () =>
          setQuestions((prev) =>
            prev.filter((item) => item.id !== question.id),
          ),
      });
    } else {
      setQuestions((prev) => prev.filter((item) => item.id !== question.id));
    }
  };

  const formatQuestions = () => {
    return questions.map(({ id, isNew, ...item }) => {
      if (isNew) {
        return item;
      }

      return { ...item, id };
    });
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();

      if (quizId) {
        await editQuizzes.mutateAsync({
          ...values,
          questions: formatQuestions(),
        });
        getQuiz();
      } else {
        const data = {
          ...values,
          questions: formatQuestions(),
        } as CreatQuizzesData;

        const result = await createQuizzes.mutateAsync(data);

        history.push(`/app/customers/crm/searchQuestionnaire/${result.id}`);
      }
    } catch {}
  };

  const columns: ColumnsType<Question> = [
    {
      dataIndex: 'questionSequence',
      title: intl.formatMessage({
        id: 'searchQuestionnaire.questions.list.sequence',
      }),
      width: 150,
      ellipsis: true,
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'searchQuestionnaire.questions.list.description',
      }),
      width: 210,
      ellipsis: true,
    },
    {
      dataIndex: 'type',
      title: intl.formatMessage({
        id: 'searchQuestionnaire.questions.list.type',
      }),
      render: (type: QuestionType) =>
        intl.formatMessage({
          id: `searchQuestionnaire.questions.list.type.${type}`,
        }),
      width: 210,
      ellipsis: true,
    },
    {
      dataIndex: 'actions',
      title: <FormattedMessage id="searchQuestionnaire.list.action" />,
      render: (_, record) => (
        <Space>
          <EditIcon
            iconProps={{ onClick: () => toggleQuestionModal(record) }}
            disabled={!hasPermissions([Authorities.CRM_QUIZ_UPDATE])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveQuestion(record)}
            disabled={!hasPermissions([Authorities.CRM_QUIZ_DELETE])}
          />
        </Space>
      ),
      ellipsis: true,
      width: 90,
      align: 'right',
      fixed: 'right',
    },
  ];

  const confirmRemoveQuiz = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'searchQuestionnaire.remove' }),
      content: `${intl.formatMessage({
        id: 'searchQuestionnaire.remove.alert',
      })} ${quiz?.description}`,
      onOk: async () => {
        await removeQuizzes.mutateAsync(Number(quizId));

        notification.success({
          message: intl.formatMessage({
            id: 'searchQuestionnaire.remove.notification.success',
          }),
          description: intl.formatMessage({
            id: 'searchQuestionnaire.remove.notification.successDescription',
          }),
        });

        history.push('/app/customers/crm/searchQuestionnaire');
      },
      okType: 'danger',
    });
  };

  const renderExtra = () => (
    <SpaceForButton>
      <Button
        type="primary"
        onClick={() => toggleQuestionModal()}
        disabled={!hasPermissions([Authorities.CRM_QUIZ_CREATE])}
        block
      >
        Nova Questão
      </Button>

      {!!quizId && (
        <DeleteButton
          onClick={() => confirmRemoveQuiz()}
          disabled={!hasPermissions([Authorities.CRM_QUIZ_DELETE])}
          block
        >
          <FormattedMessage id="commons.entity.remove" />
        </DeleteButton>
      )}

      {!quizId && (
        <Button block disabled>
          Remover
        </Button>
      )}

      <SaveButton
        onClick={onSave}
        disabled={!hasPermissions([Authorities.CRM_QUIZ_UPDATE])}
        block
      >
        Salvar
      </SaveButton>
    </SpaceForButton>
  );

  const renderContent = () => (
    <>
      <SearchQuestionnaireForm data={quiz} form={form} />

      <Divider />

      <Table<Question>
        size="middle"
        dataSource={questions}
        columns={columns}
        rowKey={(record) => String(record.id)}
        loading={loading}
        pagination={false}
        scroll={{ x: getTableScrollX(columns) }}
      />

      <Divider />

      <SpaceForButton>
        <ButtonBack path="/app/customers/crm/searchQuestionnaire" />
      </SpaceForButton>
    </>
  );

  return (
    <>
      <Helmet
        title={intl.formatMessage({
          id: 'layout.menu.crm.searchQuestionnaire',
        })}
      />

      {!!quizId && (
        <SearchQuestionnaireDetailContentTabs
          basePath={basePath}
          route={route}
          extra={renderExtra()}
        >
          {renderContent()}
        </SearchQuestionnaireDetailContentTabs>
      )}

      {!quizId && (
        <TabContentWrapper
          title={intl.formatMessage({
            id: 'layout.menu.crm.searchQuestionnaire',
          })}
          extra={renderExtra()}
        >
          {renderContent()}
        </TabContentWrapper>
      )}

      <SearchQuestionnaireQuestionModal
        open={questionModalVisible}
        onCancel={() => toggleQuestionModal()}
        onCreateQuestion={onCreateQuestion}
        onChangeQuestion={onChangeQuestion}
        data={questionDetail}
      />
    </>
  );
};

export default SearchQuestionnaireCreatePage;
